/**
 * Whether the hidden double-buffer slot may start loading the next video before the
 * frame transition ({@link VIDEO_PRELOAD_LEAD_MS} window).
 *
 * Low-capacity Android (SlimBOX / H96-class) and mid-tier video-heavy queues cannot afford
 * a second MediaCodec session overlapping the on-air clip — that path is the main cause of
 * progressive sluggishness on all-video playlists (PLAYER-1J / #74cf1a48), beyond Comlink
 * tick floods and post-hoc WebView reloads.
 */

/** Same threshold as web-tick / prophylactic recycle “video-heavy” (≥3 video creatives). */
export const VIDEO_PRELOAD_MID_VIDEO_HEAVY_MIN = 3

export interface IShouldPreloadNextVideoSlotInput {
  lowMemoryMode: boolean
  capacityTier: 'low' | 'mid' | 'high' | null | undefined
  /** Count of video creatives in the materialized queue. */
  videoFrameCountInQueue: number
  /** Ms until next frame advance; null when unknown / single-frame surface. */
  msToNextFrame: number | null
  /** Lead window from {@link VIDEO_PRELOAD_LEAD_MS}. */
  leadMs: number
}

/**
 * True only when the next-slot video may decode early without exceeding the safe
 * single-on-air decoder budget for the device/queue.
 */
export function shouldPreloadNextVideoSlot(input: IShouldPreloadNextVideoSlotInput): boolean {
  if (input.lowMemoryMode) {
    return false
  }
  if (input.capacityTier === 'low') {
    return false
  }
  if (
    input.capacityTier === 'mid'
    && input.videoFrameCountInQueue >= VIDEO_PRELOAD_MID_VIDEO_HEAVY_MIN
  ) {
    return false
  }
  const ms = input.msToNextFrame
  return ms != null && ms > 0 && ms <= input.leadMs
}
